/*
* Логирование приложения с интеграцией с бэкендом
*
* Основные функции:
* - Логирование в консоль браузера
* - Отправка логов на сервер
* - Форматирование временных меток с учетом временной зоны
* - Поддержка разных уровней логирования (debug, info, warn, error)
* - Интеграция с системой аутентификации (пропуск для логов)
*
* Особенности:
* - Кастомные хуки для отправки логов
* - Настраиваемый префикс для консольных логов
* - Обработка ошибок при отправке логов
* - Конфигурируемый уровень логирования
*/

import axios from 'axios'
import type { LogEvent, LoggerHook } from 'vue-logger-plugin'
import { createLogger } from 'vue-logger-plugin'
import { BASE_URL, TIMEZONE } from '@/environment/environment'
import { useUserStore } from '@/modules/user/stores/userStore'
import type { ICustomAxiosRequestConfig } from '@/types/ICustomAxiosRequestConfig'
import { describeAuthFailure } from '@/utils/authRecovery'

// Смещение временной зоны в миллисекундах
const timezoneOffsetMs = TIMEZONE * 60 * 60 * 1000

/**
 * Генерирует отформатированную временную метку с учетом временной зоны
 *
 * @returns {string} Строка в формате "DD.MM.YYYY HH:MM:SS"
 *
 * Пример:
 * "25.12.2023 14:30:45"
 */
export const getFormattedTimestamp = (): string => {
  const date = new Date()
  const localDate = new Date(date.getTime() + timezoneOffsetMs)

  // Вспомогательная функция для дополнения нулями
  const pad = (n: number) => n.toString().padStart(2, '0')

  // Извлечение и форматирование компонентов даты
  const day = pad(localDate.getUTCDate())
  const month = pad(localDate.getUTCMonth() + 1)
  const year = localDate.getUTCFullYear()

  // Извлечение и форматирование компонентов времени
  const hours = pad(localDate.getUTCHours())
  const minutes = pad(localDate.getUTCMinutes())
  const seconds = pad(localDate.getUTCSeconds())

  return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`
}

/**
 * Возвращает имя проекта на основе текущего хоста (субдомен или хост целиком)
 */
const getProjectName = (): string => {
  if (typeof window === 'undefined') return 'server'
  const hostname = window.location.hostname
  const parts = hostname.split('.')
  return parts[0] || hostname
}

/**
 * Карта уровней логирования для приведения к верхнему регистру
 */
const levelMap: Record<string, string> = {
  debug: 'DEBUG',
  info: 'INFO',
  warn: 'WARNING',
  error: 'ERROR',
}

const jwtLike = /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g

function redactSecrets(text: string): string {
  return text
    .replace(jwtLike, '[redacted]')
    .replace(/Bearer\s+\S+/gi, 'Bearer [redacted]')
}

/** В лог уходит текст ошибки, а не AxiosError с заголовками и телом. */
export function sanitizeLogArgument(value: unknown): unknown {
  if (value instanceof Error) {
    return redactSecrets(describeAuthFailure(value))
  }
  if (typeof value === 'string') return redactSecrets(value)
  if (Array.isArray(value)) return value.map(sanitizeLogArgument)
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>
    const sanitized: Record<string, unknown> = {}
    for (const [key, nested] of Object.entries(source)) {
      if (/^(config|request|response|headers)$/i.test(key)) continue
      if (/authorization|cookie|token|password|secret/i.test(key)) {
        sanitized[key] = '[redacted]'
        continue
      }
      sanitized[key] = sanitizeLogArgument(nested)
    }
    return sanitized
  }
  return value
}

/**
 * Хук для отправки логов на бэкенд
 *
 * @property {Function} run - Основная функция обработки события логирования
 *
 * Особенности:
 * - Отправляет POST запрос на /api/logger
 * - Пропускает проверку авторизации (skipAuth: true)
 * - Логирует ошибки отправки в консоль
 */
const sendToBackendHook: LoggerHook = {
  run(event: LogEvent) {
    // Отправляем на бэкенд только предупреждения (warn) и ошибки (error)
    if (event.level !== 'error' && event.level !== 'warn') {
      return
    }

    const mappedLevel = levelMap[event.level] || event.level.toUpperCase()
    const project = getProjectName()

    let userPrefix = ''
    try {
      const userStore = useUserStore()
      if (userStore.user) {
        const identifier = userStore.user.email || userStore.user.id || ''
        if (identifier) {
          userPrefix = `[user:${identifier}]`
        }
      }
    } catch {
      // Pinia еще не инициализирована
    }

    const messageArray: unknown[] = [`[${project}]`]
    if (userPrefix) {
      messageArray.push(userPrefix)
    }
    messageArray.push(...event.argumentArray.map(sanitizeLogArgument))

    axios.post(
      `${BASE_URL}/api/logger/`,
      {
        level: mappedLevel,
        message: messageArray,
        timestamp: getFormattedTimestamp(),
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        skipAuth: true,
      } as ICustomAxiosRequestConfig,
    ).catch(() => {
      // Игнорируем ошибку отправки лога, чтобы избежать бесконечного рекурсивного вызова console.error
    })
  },
}

/**
 * Экземпляр логгера с настройками:
 * - enabled: true - Логгер включен
 * - consoleEnabled: true - Вывод в консоль браузера
 * - level: 'debug' - Уровень логирования (debug)
 * - callerInfo: true - Информация о вызывающем коде
 * - prefixFormat: () => '[APP]' - Префикс для логов
 * - afterHooks: [sendToBackendHook] - Хук для отправки на сервер
 */
const logger = createLogger({
  enabled: true,
  consoleEnabled: true,
  level: 'debug',
  callerInfo: true,
  prefixFormat: () => '[APP]',
  afterHooks: [sendToBackendHook],
})

export { logger }
